/**
 * quoteHelpers.js
 * ------------------------------------------------------------------
 * Funzioni pure, riutilizzabili ovunque.
 * (Nessuna dipendenza da React o da Chakra UI)
 */

/* ────────────────────────────────────────────────
   UTILITY: calcola la % di sconto
──────────────────────────────────────────────── */
function computeDiscountPercent(listino = 0, riservato = 0) {
  if (listino <= 0) return 0;
  return (1 - riservato / listino) * 100; // es. 0.25 → 25 %
}

/* ────────────────────────────────────────────────
   1. Normalizza i record di prodotto
──────────────────────────────────────────────── */
export function normalizePage(records = [], currentAll = [], slas = []) {
  return records.map((p) => {
    const existing = currentAll.find((x) => x.product_code === p.product_code);

    /* prezzi mensili restituiti dall’API                                 */
    const listino = p.price_per_month    ?? 0;
    const riserv  = p.price_reserved     ?? p.reserved_per_month ?? 0;

    return {
      ...p,

      /* dati mutabili dall’utente                                        */
      quantity: existing?.quantity ?? 1,
      sla_id:   existing?.sla_id  ?? p.sla_id ?? slas[0]?.id ?? null,
      selected: existing?.selected ?? false,

      /* prezzi coerenti                                                  */
      price_per_month:    listino,
      reserved_per_month: riserv,

      /* percentuale di sconto                                            */
      discount_percent:   computeDiscountPercent(listino, riserv),
    };
  });
}

/* ────────────────────────────────────────────────
   2. Estrarre filtri dinamici univoci
──────────────────────────────────────────────── */
export function extractDynamicFilters(list = []) {
  const uniq = {
    brands:        new Map(),
    categories:    new Map(),
    subCategories: new Map(),
    families:      new Map(),
  };

  list.forEach((p) => {
    if (p.brand)        uniq.brands       .set(p.brand.id,        p.brand);
    if (p.category)     uniq.categories   .set(p.category.id,     p.category);
    if (p.subCategory)  uniq.subCategories.set(p.subCategory.id,  p.subCategory);
    if (p.family)       uniq.families     .set(p.family.id,       p.family);
  });

  return Object.fromEntries(
    Object.entries(uniq).map(([k, m]) => [k, Array.from(m.values())])
  );
}

/* ────────────────────────────────────────────────
   3. Filtro → sort (per listino/mese) → paginazione
──────────────────────────────────────────────── */
export function filterAndPaginate({
  list               = [],
  filters             = {},
  productCode         = "",
  searchText          = "",
  showSelectedOnly    = false,
  currentPage         = 1,
  itemsPerPage        = 15,
}) {
  const { brand_id, categories_id, subcategory_id, family_id } = filters;

  let out = list
    .filter((p) => !brand_id       || p.brand?.id       .toString() === brand_id)
    .filter((p) => !categories_id  || p.category?.id    .toString() === categories_id)
    .filter((p) => !subcategory_id || p.subCategory?.id .toString() === subcategory_id)
    .filter((p) => !family_id      || p.family?.id      .toString() === family_id);

  if (productCode) {
    const q = productCode.toLowerCase();
    out = out.filter((p) => p.product_code.toLowerCase().includes(q));
  }
  if (searchText) {
    const q = searchText.toLowerCase();
    out = out.filter((p) => p.description.toLowerCase().includes(q));
  }
  if (showSelectedOnly) out = out.filter((p) => p.selected);

  /* ordinamento dal listino più basso al più alto                       */
  out.sort((a, b) => (a.price_per_month ?? 0) - (b.price_per_month ?? 0));

  const total = out.length;
  const start = (currentPage - 1) * itemsPerPage;

  return { total, page: out.slice(start, start + itemsPerPage) };
}

/* ────────────────────────────────────────────────
   4. Riepilogo economico
──────────────────────────────────────────────── */
export function calcSummary(selected = [], periodicity = null) {
  if (!periodicity) return null;

  let imponibile = 0,
      sconti     = 0,
      netto      = 0,
      opzioni    = 0;

  selected.forEach((p) => {
    imponibile += p.totale ?? 0;
    sconti     += (p.totale ?? 0) - (p.totale_scontato ?? 0);
    netto      += p.totale_scontato ?? 0;

    (p.options || []).forEach((o) => {
      opzioni += (o.price_per_month_option ?? 0) * (p.quantity ?? 1);
    });
  });

  const iva    = (netto + opzioni) * 0.22;
  const totale = netto + iva + opzioni;

  return {
    imponibile,
    totaleSconti:  sconti,
    netto,
    totaleOpzioni: opzioni,
    iva,
    totale,
  };
}

/* =========================================================================
   Funzioni “legacy” usate solo da Quotes.jsx
   (invariate rispetto al tuo file originale)
   ========================================================================= */

/** Filtro senza paginazione, usato dalla barra di ricerca in Quotes.jsx   */
export function filterQuotes(list, term, dStart, dEnd) {
  let out = [...list];

  if (term) {
    const t = term.toLowerCase();
    out = out.filter(
      (q) =>
        q.id.toString().includes(t) ||
        q.note?.toLowerCase().includes(t) ||
        q.total_amount.toString().includes(t) ||
        q.discount_extra.toString().includes(t)
    );
  }

  if (dStart || dEnd) {
    out = out.filter((q) => {
      const d = new Date(q.date);
      if (dStart && d < dStart) return false;
      if (dEnd) {
        const e = new Date(dEnd);
        e.setHours(23, 59, 59, 999);
        if (d > e) return false;
      }
      return true;
    });
  }

  return out;
}

/** Ordina l’array in base al sortConfig                                 */
export function sortQuotes(arr = [], { key = "", direction = "asc" } = {}) {
  if (!key) return arr;

  return [...arr].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    const aNum = parseFloat(aVal);
    const bNum = parseFloat(bVal);

    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
      return direction === "asc" ? aNum - bNum : bNum - aNum;
    }

    return direction === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });
}

/** Restituisce il “pezzo” di array relativo alla pagina corrente        */
export function paginate(arr = [], currentPage = 1, itemsPerPage = 15) {
  const start = (currentPage - 1) * itemsPerPage;
  return arr.slice(start, start + itemsPerPage);
}
